import { NO_STEP_DATA_LABEL, stepLabelFor, type SignalVerificationState } from '@shared/flow/signal-verification';
import type { SignalRowActionChip } from './rowActions';
import type { CardSignal } from './useSignalActionCard';

/**
 * Pure presentation helpers lifted out of `SignalActionCard.tsx` (bead
 * terra-design-v2-drilldowns-ppn.14, coding-standards.md §1 "lift once, V1
 * imports it back") so the new Glaze `SignalLeadCard`
 * (`shell-next/entity/SignalLeadCard.tsx`) can build the identical label/
 * status-line strings without re-deriving them. `SignalActionCard.tsx`
 * imports every one of these back — this file is the only definition site.
 */

/** Badge tone for the Signal card's state chip (D-A/D-I) — the row itself keeps its own Flow status column, so this chip is not rendered a second time there. */
export const CHIP_COLOR: Record<SignalRowActionChip['kind'], 'secondary' | 'yellow' | 'ghost'> = {
  grey: 'secondary',
  amber: 'yellow',
  muted: 'ghost',
};

export function stepLabel(signal: CardSignal): string {
  return stepLabelFor(signal.signal_tactic) ?? signal.signal_tactic ?? NO_STEP_DATA_LABEL;
}

/**
 * The dropdown option label's OWN step segment (item 5) — unlike
 * `stepLabel` (the meta line's Step display, which always says SOMETHING,
 * falling back to `NO_STEP_DATA_LABEL`), this returns `null` when there is
 * no real step info so `optionLabel` can omit the segment entirely rather
 * than repeat "No step data" a second time next to the meta line's own
 * chip/step text for the same signal.
 */
export function optionStepSegment(signal: CardSignal): string | null {
  return stepLabelFor(signal.signal_tactic) ?? signal.signal_tactic ?? null;
}

/**
 * The flow segment of the option label / meta line. Only the clicked row
 * carries `flow_name` (the contact-signals route does not project it); a
 * flowed row without one omits the segment rather than claiming "No flow".
 */
export function flowLabel(signal: CardSignal): string | null {
  if ('flow_name' in signal && signal.flow_name) return signal.flow_name;
  if (signal.signal_status === 'No flow' || !signal.signal_instance_id) return 'No flow';
  return null;
}

/** Item 5 — source type + flow + step, each included only when it carries information. */
export function optionLabel(signal: CardSignal): string {
  return [signal.signal_source_type ?? signal.signal_type, flowLabel(signal), optionStepSegment(signal)].filter(Boolean).join(' · ');
}

/**
 * The relative-time suffix of the Gong status line — `just now`, `N min ago`,
 * `N hr(s) ago`, `N day(s) ago`. `null` for a missing/unparseable timestamp:
 * the caller then says "never checked", never a guessed time.
 */
export function agoLabel(verifiedAt: string | null, now: Date): string | null {
  if (!verifiedAt) return null;
  const verifiedMs = Date.parse(verifiedAt);
  if (Number.isNaN(verifiedMs)) return null;
  const diffMin = Math.max(0, Math.round((now.getTime() - verifiedMs) / 60_000));
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hr${diffHr === 1 ? '' : 's'} ago`;
  const diffDay = Math.round(diffHr / 24);
  return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
}

export interface GongStatusLineInput {
  /** `getSignalRowActions(selected, now).state` — the resolver's verdict. */
  state: SignalVerificationState;
  /** The row's `gong_verified_at`. */
  verifiedAt: string | null;
  now: Date;
  /** A refresh (and so a live Gong check) is running right now (`useGongCheckActivity`). */
  inFlight: boolean;
  /** The last refresh failed or its Gong check did not complete (`useGongCheckActivity`). */
  lastCheckFailed: boolean;
}

/**
 * The Signal card's ONE Gong status sentence — qa-batch4-plan.md D-B's table,
 * transcribed. Total over `SignalVerificationState`; `null` = no line
 * (closed / snoozed / no flow). In flight wins over failed (a retry is
 * running). `removed`/`paused` with no timestamp (snapshot-only status) and
 * any state without a parseable `verifiedAt` fall to the never-checked rows.
 * The stale-idle wording deliberately gives no instruction: inside the
 * refresh throttle window the Refresh button re-checks nothing (plan §1.4 /
 * REVIEW-THESE R3), so "press Refresh" would sometimes be untrue.
 */
export function gongStatusLine({ state, verifiedAt, now, inFlight, lastCheckFailed }: GongStatusLineInput): string | null {
  if (state === 'closed' || state === 'snoozed' || state === 'no_flow') return null;
  const ago = agoLabel(verifiedAt, now);
  if (ago) {
    if (state === 'live_manual' || state === 'live_auto' || state === 'live_unknown_step') return `Gong confirmed ${ago}`;
    if (state === 'removed' || state === 'paused') return `Gong checked ${ago}`;
    if (inFlight) return `Gong last checked ${ago} — rechecking…`;
    if (lastCheckFailed) return `Gong last checked ${ago} — recheck failed, press Refresh to try again`;
    return `Gong last checked ${ago}`;
  }
  if (inFlight) return 'Checking with Gong…';
  if (lastCheckFailed) return "Gong couldn't be checked — press Refresh to try again";
  return "Gong hasn't checked this task yet";
}
